import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Info, Trophy, Target, Flag, Calendar, Award, Loader2 } from 'lucide-react';
import { getSeasonSettings } from '../services/api';

// Base scoring rules that are always shown
const baseScoringRules = [
  // Race positie punten (gebaseerd op verschil)
  { category: 'Coureur in juiste positie', points: 25, setting: 'race_top5_enabled' },
  { category: 'Coureur 1 positie verschil (binnen top 10)', points: 18, setting: 'race_top5_enabled' },
  { category: 'Coureur 2 posities verschil (binnen top 10)', points: 15, setting: 'race_top5_enabled' },
  { category: 'Coureur 3 posities verschil (binnen top 10)', points: 12, setting: 'race_top5_enabled' },
  { category: 'Coureur 4 posities verschil (binnen top 10)', points: 10, setting: 'race_top5_enabled' },
  { category: 'Coureur 5 posities verschil (binnen top 10)', points: 8, setting: 'race_top5_enabled' },
  { category: 'Coureur 6 posities verschil (binnen top 10)', points: 6, setting: 'race_top5_enabled' },
  { category: 'Coureur 7 posities verschil (binnen top 10)', points: 4, setting: 'race_top5_enabled' },
  { category: 'Coureur 8 posities verschil (binnen top 10)', points: 2, setting: 'race_top5_enabled' },
  { category: 'Coureur 9 posities verschil (binnen top 10)', points: 1, setting: 'race_top5_enabled' },
];

// Conditional scoring rules based on settings
const conditionalScoringRules = [
  // Kampioenschap - Top 3 voorspelling
  { category: 'WK/CC: Positie exact juist voorspeld', points: 50, setting: 'world_champion_enabled' },
  { category: 'WK/CC: 1 positie verschil', points: 40, setting: 'world_champion_enabled' },
  { category: 'WK/CC: 2 posities verschil', points: 30, setting: 'world_champion_enabled' },
  // Race bonus punten
  { category: 'Snelste ronde correct', points: 10, setting: 'fastest_lap_enabled' },
  { category: 'Safety Car correct', points: 10, setting: 'safety_car_enabled' },
  { category: 'Virtual Safety Car correct', points: 10, setting: 'virtual_safety_car_enabled' },
  { category: 'Aantal DNF correct **', points: 10, setting: 'dnf_count_enabled' },
  { category: 'Regen race correct *', points: 10, setting: 'rain_race_enabled' },
  // Kwalificatie P1 punten (qualifying_top3_enabled)
  { category: 'Kwalificatie P1 correct', points: 10, setting: 'qualifying_top3_enabled' },
  // Kwalificatie P2 en P3 (qualifying_p2p3_enabled - aparte toggle)
  { category: 'Kwalificatie P2 correct', points: 10, setting: 'qualifying_p2p3_enabled' },
  { category: 'Kwalificatie P3 correct', points: 10, setting: 'qualifying_p2p3_enabled' },
  // Pole tijd punten
  { category: 'Pole tijd exact correct', points: 100, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 0.001 sec', points: 75, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 0.005 sec', points: 50, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 0.010 sec', points: 40, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 0.100 sec', points: 30, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 0.200 sec', points: 25, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 0.300 sec', points: 18, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 0.400 sec', points: 15, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 0.500 sec', points: 12, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 0.700 sec', points: 10, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 0.900 sec', points: 8, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 1.350 sec', points: 6, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 1.800 sec', points: 4, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 2.250 sec', points: 2, setting: 'pole_time_enabled' },
  { category: 'Pole tijd binnen 2.700 sec', points: 1, setting: 'pole_time_enabled' },
  { category: 'Pole tijd dichtstbijzijnde (als niemand binnen 2.7s) ***', points: 10, setting: 'pole_time_enabled' },
  // Sprint weekend punten
  { category: 'Sprint Race winnaar correct', points: 10, setting: 'sprint_race_enabled' },
  { category: 'Sprint Kwalificatie winnaar (Pole) correct', points: 10, setting: 'sprint_qualifying_enabled' },
  { category: 'Sprint Pole tijd exact correct', points: 40, setting: 'sprint_pole_winner_enabled' },
  { category: 'Sprint Pole tijd binnen 0.5 sec', points: 10, setting: 'sprint_pole_winner_enabled' },
];

const Speluitleg = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getSeasonSettings(2026);
        setSettings(data);
      } catch (error) {
        console.error('Error loading settings:', error);
        // Default to all enabled if can't load
        setSettings({
          race_top5_enabled: true,
          qualifying_top3_enabled: true,
          qualifying_p2p3_enabled: true,
          pole_time_enabled: true,
          fastest_lap_enabled: true,
          safety_car_enabled: true,
          virtual_safety_car_enabled: true,
          dnf_count_enabled: true,
          rain_race_enabled: true,
          world_champion_enabled: true,
          constructor_champion_enabled: true,
          sprint_race_enabled: true,
          sprint_qualifying_enabled: true,
          sprint_pole_winner_enabled: true,
        });
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  // Filter scoring rules based on settings
  const getFilteredScoringRules = () => {
    if (!settings) return [];
    
    const filteredBase = baseScoringRules.filter(rule => 
      !rule.setting || settings[rule.setting] === true
    );
    
    const filteredConditional = conditionalScoringRules.filter(rule => 
      settings[rule.setting] === true
    );
    
    return [...filteredBase, ...filteredConditional];
  };

  // Check if any sprint features are enabled
  const isSprintEnabled = settings && (
    settings.sprint_race_enabled || 
    settings.sprint_qualifying_enabled || 
    settings.sprint_pole_winner_enabled
  );

  // Check if championship predictions are enabled
  const isChampionshipEnabled = settings && (
    settings.world_champion_enabled || 
    settings.constructor_champion_enabled
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-8 flex items-center justify-center">
        <Loader2 className="animate-spin h-8 w-8 text-[#FDB913]" />
      </div>
    );
  }

  const filteredRules = getFilteredScoringRules();

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-block bg-[#FDB913] rounded-full p-4 mb-4">
            <Info size={40} className="text-[#1a2332]" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-2">Speluitleg</h1>
          <p className="text-muted-foreground">Leer hoe je punten kunt verdienen</p>
        </div>

        {/* How to Play */}
        <Card className="mb-8 border-2 border-[#FDB913]">
          <CardHeader className="bg-gradient-to-r from-[#1a2332] to-[#2d3e50]">
            <CardTitle className="text-[#FDB913] flex items-center space-x-2">
              <Trophy size={24} />
              <span>Hoe Werkt Het?</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="bg-[#FDB913] rounded-full p-2 flex-shrink-0">
                  <span className="text-[#1a2332] font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1 text-foreground">Maak een Account</h3>
                  <p className="text-muted-foreground">Registreer je en log in op het platform.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-[#FDB913] rounded-full p-2 flex-shrink-0">
                  <span className="text-[#1a2332] font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1 text-foreground">Maak Je Voorspellingen</h3>
                  <p className="text-muted-foreground">
                    Voor elke Grand Prix voorspel je de top 5 finishers
                    {settings?.qualifying_top3_enabled && ' en kwalificatie top 3'}.
                    Voorspellingen moeten vóór aanvang Vrije Training 1 zijn ingediend.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-[#FDB913] rounded-full p-2 flex-shrink-0">
                  <span className="text-[#1a2332] font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1 text-foreground">Verzamel Punten</h3>
                  <p className="text-muted-foreground">
                    Na de race worden punten toegekend op basis van de nauwkeurigheid van je voorspellingen.
                    Hoe preciezer, hoe meer punten!
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-[#FDB913] rounded-full p-2 flex-shrink-0">
                  <span className="text-[#1a2332] font-bold">4</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1 text-foreground">Klim in het Klassement</h3>
                  <p className="text-muted-foreground">
                    Verzamel punten gedurende het hele seizoen. De spelers met de meeste punten winnen prijzen!
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prediction Types */}
        <Card className="mb-8 border-2 border-[#FDB913]">
          <CardHeader className="bg-gradient-to-r from-[#1a2332] to-[#2d3e50]">
            <CardTitle className="text-[#FDB913] flex items-center space-x-2">
              <Target size={24} />
              <span>Soorten Voorspellingen</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-6">
              {/* Race Predictions - Always shown */}
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Flag className="text-[#FDB913]" size={20} />
                  <h3 className="font-bold text-lg text-foreground">Race Voorspellingen</h3>
                </div>
                <p className="text-muted-foreground ml-7">
                  Voorspel de top 5 finishers van de race in de juiste volgorde.
                </p>
              </div>

              {/* Qualifying Predictions - Only if enabled */}
              {settings?.qualifying_top3_enabled && (
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Target className="text-[#FDB913]" size={20} />
                    <h3 className="font-bold text-lg text-foreground">Kwalificatie Voorspellingen</h3>
                  </div>
                  <p className="text-muted-foreground ml-7">
                    Voorspel wie de pole position pakt en de top 3 van de kwalificatie.
                  </p>
                </div>
              )}

              {/* Championship Predictions - Only if enabled */}
              {isChampionshipEnabled && (
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Trophy className="text-[#FDB913]" size={20} />
                    <h3 className="font-bold text-lg text-foreground">Seizoen Voorspellingen</h3>
                  </div>
                  <p className="text-muted-foreground ml-7 mb-2">
                    Voorspel de top 3 van het eindklassement voor:
                    {settings?.world_champion_enabled && ' Wereldkampioenschap (coureurs)'}
                    {settings?.world_champion_enabled && settings?.constructor_champion_enabled && ' en'}
                    {settings?.constructor_champion_enabled && ' Constructeurs Kampioenschap'}
                    . Deze voorspelling kun je maken tot aan de eerste race.
                  </p>
                  <div className="ml-7 bg-muted/50 p-3 rounded-lg text-sm">
                    <p className="font-semibold mb-1">Puntentelling:</p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1">
                      <li>Positie exact juist: <span className="text-[#FDB913] font-semibold">50 punten</span></li>
                      <li>1 positie verschil: <span className="text-[#FDB913] font-semibold">40 punten</span></li>
                      <li>2 posities verschil: <span className="text-[#FDB913] font-semibold">30 punten</span></li>
                    </ul>
                    <p className="mt-2 text-xs text-muted-foreground italic">
                      De Admin voert de definitieve top 5 in als resultaat na afloop van het seizoen.
                    </p>
                  </div>
                </div>
              )}

              {/* Sprint Weekend Predictions - Only if any sprint setting is enabled */}
              {isSprintEnabled && (
                <div className="bg-purple-100 dark:bg-purple-900/30 p-4 rounded-lg border-2 border-purple-400 dark:border-purple-500">
                  <div className="flex items-center space-x-2 mb-2">
                    <Flag className="text-purple-600 dark:text-purple-400" size={20} />
                    <h3 className="font-bold text-lg text-purple-700 dark:text-purple-300">🏎️ Sprint Weekend Voorspellingen</h3>
                  </div>
                  <p className="text-muted-foreground ml-7 mb-3">
                    Tijdens sprint weekenden kun je extra voorspellingen doen en extra punten verdienen!
                  </p>
                  <ul className="ml-7 space-y-2 text-foreground">
                    {settings?.sprint_race_enabled && (
                      <li className="flex items-center space-x-2">
                        <span className="bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 px-2 py-1 rounded text-sm font-semibold">10 pts</span>
                        <span><strong>Sprint Race Winnaar</strong> - Voorspel wie de sprint race wint</span>
                      </li>
                    )}
                    {settings?.sprint_qualifying_enabled && (
                      <li className="flex items-center space-x-2">
                        <span className="bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 px-2 py-1 rounded text-sm font-semibold">10 pts</span>
                        <span><strong>Sprint Kwalificatie Winnaar</strong> - Voorspel wie pole pakt in de sprint kwalificatie</span>
                      </li>
                    )}
                    {settings?.sprint_pole_winner_enabled && (
                      <li className="flex items-start space-x-2">
                        <span className="bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 px-2 py-1 rounded text-sm font-semibold whitespace-nowrap">10-40 pts</span>
                        <span><strong>Sprint Pole Tijd</strong> - Voorspel de snelste tijd in de sprint kwalificatie (exact = 40 pts, binnen 0.5 sec = 10 pts)</span>
                      </li>
                    )}
                  </ul>
                  <p className="text-sm text-purple-600 dark:text-purple-300 mt-3 ml-7">
                    <strong>Let op:</strong> Sprint voorspellingen zijn alleen beschikbaar tijdens sprint weekenden. 
                    In 2026 zijn er 6 sprint weekenden gepland.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Scoring System */}
        <Card className="mb-8 border-2 border-[#FDB913]">
          <CardHeader className="bg-gradient-to-r from-[#1a2332] to-[#2d3e50]">
            <CardTitle className="text-[#FDB913] flex items-center space-x-2">
              <Award size={24} />
              <span>Puntensysteem</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">Categorie</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-muted-foreground">Punten</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRules.map((rule, index) => (
                    <tr
                      key={rule.category}
                      className={`${
                        index % 2 === 0 ? 'bg-card' : 'bg-secondary/50'
                      } border-b border-border`}
                    >
                      <td className="px-4 py-3 text-foreground">{rule.category}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-bold text-[#FDB913]">{rule.points}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Notes - Only show relevant notes */}
            <div className="mt-6 bg-secondary p-4 rounded-lg">
              <h4 className="font-bold text-foreground mb-2">Toelichting:</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {settings?.rain_race_enabled && (
                  <li>
                    <span className="font-semibold">* Regen race:</span> Een race wordt pas aangemerkt als Regenrace wanneer minimaal één coureur één volledige ronde met INTERMEDIATES (groene rand) of FULL WETS (blauwe rand) gereden is. Pit in, Intermediates / Wet, Pit uit, ronde en direct weer Pit in telt als één volledige ronde.
                  </li>
                )}
                {settings?.dnf_count_enabled && (
                  <li>
                    <span className="font-semibold">** DNF:</span> Een DNF (Did Not Finish) krijgt een rijder wanneer de race gestart is en tijdens de race uitgevallen is. Een DNS (Did Not Start) wordt niet gerekend als DNF. Een coureur dient de race gestart te hebben om vervolgens tijdens de race uit te vallen om tot een DNF aangemerkt te worden.
                  </li>
                )}
                {settings?.pole_time_enabled && (
                  <>
                    <li>
                      <span className="font-semibold">*** Pole-tijd fallback:</span> Als geen enkele deelnemer de pole-tijd binnen 2.700 seconden voorspelt, krijgt degene die het dichtst bij de werkelijke tijd zit <span className="text-[#FDB913] font-semibold">10 punten</span>.
                    </li>
                    <li>
                      <span className="font-semibold">Kwalificatie:</span> Wanneer de kwalificatie door weersomstandigheden niet verreden wordt of uitgesteld/afgelast wordt, dan zullen de voorspellingen van de pole-tijd niet meetellen.
                    </li>
                    <li>
                      <span className="font-semibold">Pole-tijd formaat:</span> MM:SS.mmm (bijvoorbeeld 01:30.456)
                    </li>
                  </>
                )}
                {isSprintEnabled && (
                  <li>
                    <span className="font-semibold text-purple-400">Sprint Weekend:</span> Een sprint weekend heeft een aangepast schema met sprint kwalificatie en sprint race op zaterdag. De sprint race is korter dan de hoofdrace (ongeveer 100km).
                    {settings?.sprint_race_enabled && ' Je kunt punten verdienen door de sprint winnaar correct te voorspellen (10 pts).'}
                    {settings?.sprint_qualifying_enabled && ' Sprint kwalificatie winnaar voorspellen levert 10 pts op.'}
                    {settings?.sprint_pole_winner_enabled && ' Sprint pole tijd voorspellen kan tot 40 pts opleveren (exact) of 10 pts (binnen 0.5 sec).'}
                  </li>
                )}
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Rules */}
        <Card className="mb-8 border-2 border-[#FDB913]">
          <CardHeader className="bg-gradient-to-r from-[#1a2332] to-[#2d3e50]">
            <CardTitle className="text-[#FDB913] flex items-center space-x-2">
              <Calendar size={24} />
              <span>Belangrijke Regels</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <span className="text-[#FDB913] font-bold">•</span>
                <span className="text-muted-foreground">
                  Voorspellingen moeten vóór aanvang Vrije Training 1 zijn ingediend
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FDB913] font-bold">•</span>
                <span className="text-muted-foreground">
                  Je kunt je voorspelling niet meer wijzigen na de deadline
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FDB913] font-bold">•</span>
                <span className="text-muted-foreground">
                  Voor hoofdprijzen moet je minimaal 10 races voorspellen
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FDB913] font-bold">•</span>
                <span className="text-muted-foreground">
                  Bij een gelijk aantal punten wint de speler met de meeste exacte voorspellingen
                </span>
              </li>
              {isChampionshipEnabled && (
                <li className="flex items-start space-x-2">
                  <span className="text-[#FDB913] font-bold">•</span>
                  <span className="text-muted-foreground">
                    Kampioenschap voorspellingen kunnen gewijzigd worden tot de eerste race
                  </span>
                </li>
              )}
              <li className="flex items-start space-x-2">
                <span className="text-[#FDB913] font-bold">•</span>
                <span className="text-muted-foreground">
                  Officiële F1 resultaten zijn leidend voor puntentoekenning
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Jaarbonus */}
        <Card className="mb-8 border-2 border-purple-500">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-800">
            <CardTitle className="text-white flex items-center space-x-2">
              <Trophy size={24} />
              <span>Jaarbonus</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-muted-foreground mb-4">
              Naast de reguliere prijzenpot is er een speciale <strong className="text-purple-600 dark:text-purple-400">Jaarbonus</strong> voor 
              de speler met de beste prestatie in één enkele race.
            </p>
            
            <div className="space-y-4">
              <div className="bg-purple-100 dark:bg-purple-900/30 p-4 rounded-lg">
                <h4 className="font-bold text-purple-700 dark:text-purple-300 mb-2">Hoe win je de Jaarbonus?</h4>
                <p className="text-muted-foreground">
                  De speler met de <strong>hoogste totale score in één enkele race</strong> (race + kwalificatie punten) 
                  gedurende het gehele seizoen wint de Jaarbonus.
                </p>
              </div>
              
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-300 dark:border-amber-700">
                <h4 className="font-bold text-amber-700 dark:text-amber-300 mb-2 flex items-center space-x-2">
                  <Award size={20} />
                  <span>Gedoogbeleid (max €449 per prijs)</span>
                </h4>
                <p className="text-muted-foreground mb-3">
                  Conform de Nederlandse wet op kansspelen mag een prijs niet meer dan €449 bedragen. 
                  Als de Jaarbonus pot hoger is, wordt deze verdeeld over meerdere winnaars:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start space-x-2">
                    <span className="text-amber-600 font-bold">1.</span>
                    <span><strong>1e prijs:</strong> De speler met de hoogste score in één race ontvangt maximaal €449</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-amber-600 font-bold">2.</span>
                    <span><strong>2e prijs:</strong> De speler met de op-één-na-hoogste score ontvangt het restant (max €449)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-amber-600 font-bold">3.</span>
                    <span><strong>Etc:</strong> Dit herhaalt zich totdat de volledige pot verdeeld is</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-300 dark:border-blue-700">
                <h4 className="font-bold text-blue-700 dark:text-blue-300 mb-2">Bij gelijke scores</h4>
                <p className="text-muted-foreground">
                  Als meerdere spelers dezelfde hoogste score in een race hebben behaald, wordt hun gezamenlijke 
                  prijsbedrag gelijk verdeeld. <br />
                  <em className="text-sm">Voorbeeld: Als 2 spelers allebei de hoogste score hebben en in aanmerking 
                  komen voor €449 + €400, ontvangen ze elk €424,50.</em>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="border-2 border-[#FDB913]">
          <CardHeader className="bg-gradient-to-r from-[#1a2332] to-[#2d3e50]">
            <CardTitle className="text-[#FDB913] flex items-center space-x-2">
              <Info size={24} />
              <span>Tips voor Succes</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <span className="text-[#FDB913] font-bold">💡</span>
                <span className="text-muted-foreground">
                  Volg de Vrije Trainingen om de snelheid van teams in te schatten
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FDB913] font-bold">💡</span>
                <span className="text-muted-foreground">
                  Houd rekening met de weersvoorspelling voor elke race
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FDB913] font-bold">💡</span>
                <span className="text-muted-foreground">
                  Bekijk historische data van coureurs op specifieke circuits
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FDB913] font-bold">💡</span>
                <span className="text-muted-foreground">
                  Denk aan de strategie: sommige circuits zijn bekend om veel DNF's
                </span>
              </li>
              {isSprintEnabled && (
                <li className="flex items-start space-x-2">
                  <span className="text-purple-500 font-bold">💡</span>
                  <span className="text-muted-foreground">
                    Tijdens sprint weekenden zijn er extra punten te verdienen - vergeet deze voorspellingen niet!
                  </span>
                </li>
              )}
            </ul>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <Card className="border-2 border-amber-400 bg-amber-50/50 dark:bg-amber-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center text-amber-700 dark:text-amber-400">
              <Info className="mr-2" size={20} />
              Disclaimer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-amber-800 dark:text-amber-300 leading-relaxed">
              De organisator van de Formule 1 Pool doet zijn uiterste best om de pool correct en eerlijk te laten verlopen. 
              Echter, bij technische storingen, uitval van het systeem, software fouten, of andere onvoorziene omstandigheden 
              behoudt de organisator zich het recht voor om de pool op een later moment <strong>handmatig te actualiseren</strong> en/of 
              te corrigeren.
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-300 mt-3 leading-relaxed">
              Door deel te nemen aan deze pool ga je akkoord met deze voorwaarden. De beslissing van de organisator is in alle gevallen bindend.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Speluitleg;
